import { Menu as Base } from "@base-ui/react/menu";
import type { ComponentProps, ReactNode } from "react";

import "./surface.css";
import "./menu.css";
import { cn } from "./cn";

export type MenuSide = "top" | "bottom" | "left" | "right";
export type MenuAlign = "start" | "center" | "end";

interface MenuContentProps {
  children: ReactNode;
  side?: MenuSide;
  align?: MenuAlign;
  sideOffset?: number;
  className?: string;
}

/** Portal + positioned, token-styled menu surface. Arrow-key roving, Enter to select, Escape
 * to close, and focus restore are all handled by Base UI's headless Menu. */
function MenuContent({
  children,
  side = "bottom",
  align = "start",
  sideOffset = 6,
  className,
}: MenuContentProps) {
  return (
    <Base.Portal>
      <Base.Positioner
        className="rc-menu-positioner"
        side={side}
        align={align}
        sideOffset={sideOffset}
      >
        <Base.Popup className={cn("rc-surface", "rc-menu", className)}>{children}</Base.Popup>
      </Base.Positioner>
    </Base.Portal>
  );
}

interface MenuItemProps extends Omit<ComponentProps<typeof Base.Item>, "className"> {
  /** Renders the item in the danger tone (destructive action). */
  destructive?: boolean;
  className?: string;
}

function MenuItem({ destructive = false, className, ...rest }: MenuItemProps) {
  return (
    <Base.Item
      className={cn("rc-menu-item", destructive && "rc-menu-item--destructive", className)}
      {...rest}
    />
  );
}

function MenuSeparator({
  className,
  ...rest
}: Omit<ComponentProps<typeof Base.Separator>, "className"> & { className?: string }) {
  return <Base.Separator className={cn("rc-menu-separator", className)} {...rest} />;
}

/**
 * Token-styled dropdown menu on Base UI's headless Menu. Compound API:
 * `<Menu.Root><Menu.Trigger render={<Button/>} /><Menu.Content><Menu.Item/>…</Menu.Content></Menu.Root>`.
 */
export const Menu = {
  Root: Base.Root,
  Trigger: Base.Trigger,
  Content: MenuContent,
  Item: MenuItem,
  Separator: MenuSeparator,
  Group: Base.Group,
  GroupLabel: Base.GroupLabel,
};
